import { roundMinor } from './money.js';
/** Ascending tier order, so "next tier" and comparisons are unambiguous. */
export const TIER_ORDER = ['SILVER', 'GOLD', 'PLATINUM'];
/**
 * The programme as first configured. Cloud Functions read the live doc and fall
 * back to this; the admin config page seeds from it. Silver 0.25 / Gold 0.5 /
 * Platinum 0.75, per the brief.
 */
export const DEFAULT_LOYALTY_CONFIG = {
    tiers: [
        {
            tier: 'SILVER',
            earnRateBps: 25,
            minSpend12mMinor: 0,
            label: 'Silver',
            perks: ['Earn Print Credits on every order', 'Credits valid for 12 months'],
        },
        {
            tier: 'GOLD',
            earnRateBps: 50,
            minSpend12mMinor: 2_500_000, // ₹25,000
            label: 'Gold',
            perks: ['Earn Print Credits faster', 'Priority production on eligible orders'],
        },
        {
            tier: 'PLATINUM',
            earnRateBps: 75,
            minSpend12mMinor: 10_000_000, // ₹1,00,000
            label: 'Platinum',
            perks: ['Our fastest credit earning', 'Priority production', 'Early access to new products'],
        },
    ],
    earnBase: 'NET_ITEMS_EX_TAX',
    redeemCapBps: 5000, // up to 50% of an order
    minRedeemMinor: 1000, // ₹10
    expiryMonths: 12,
    updatedAt: 0,
};
/** Strip the private config down to what a customer may see (no earn rate). */
export function toPublicProgram(config) {
    return {
        tiers: config.tiers.map(({ tier, label, minSpend12mMinor, perks }) => ({
            tier,
            label,
            minSpend12mMinor,
            perks,
        })),
        redeemCapBps: config.redeemCapBps,
        minRedeemMinor: config.minRedeemMinor,
        expiryMonths: config.expiryMonths,
    };
}
/* ------------------------------------------------------------------ *
 * Tiers
 * ------------------------------------------------------------------ */
export function tierConfigFor(config, tier) {
    const found = config.tiers.find((t) => t.tier === tier) ?? config.tiers[0];
    // A misconfigured (empty) tier list must never throw or earn — fall back to a
    // zero-rate Silver so the worst case is "no credits", never a crash or a leak.
    return found ?? { tier: 'SILVER', earnRateBps: 0, minSpend12mMinor: 0, label: 'Silver', perks: [] };
}
/** The highest tier whose spend threshold the customer has met. */
export function tierForSpend(config, spend12mMinor) {
    let result = config.tiers[0]?.tier ?? 'SILVER';
    for (const t of config.tiers) {
        if (spend12mMinor >= t.minSpend12mMinor)
            result = t.tier;
    }
    return result;
}
/** The next tier up and the spend still needed for it — for "spend ₹X to reach Gold". */
export function spendToNextTier(config, spend12mMinor) {
    const current = tierForSpend(config, spend12mMinor);
    const currentIdx = TIER_ORDER.indexOf(current);
    const next = config.tiers.find((t) => TIER_ORDER.indexOf(t.tier) === currentIdx + 1);
    if (!next)
        return null; // already at the top tier
    return { next, remainingMinor: Math.max(0, next.minSpend12mMinor - spend12mMinor) };
}
/* ------------------------------------------------------------------ *
 * Earning
 * ------------------------------------------------------------------ */
/**
 * Credits granted for one paid order. `earnBaseMinor` is chosen by the caller
 * from the order totals per `config.earnBase` (taxable value, or grand total) —
 * this function only applies the tier rate, so the rate stays in one place.
 */
export function earnedCreditsMinor(params) {
    const rateBps = tierConfigFor(params.config, params.tier).earnRateBps;
    if (params.earnBaseMinor <= 0 || rateBps <= 0)
        return 0;
    return roundMinor((params.earnBaseMinor * rateBps) / 10_000);
}
/** When a lot earned at `now` expires, honouring `config.expiryMonths`. */
export function expiryFrom(now, expiryMonths) {
    const d = new Date(now);
    d.setMonth(d.getMonth() + expiryMonths);
    return d.getTime();
}
/* ------------------------------------------------------------------ *
 * Redeeming
 * ------------------------------------------------------------------ */
/**
 * How much credit may actually be applied to an order: never more than the
 * balance, never more than the per-order cap. A balance below `minRedeemMinor`
 * can't be redeemed at all. `orderValueMinor` is the value redemption is capped
 * against (the pre-credit item value) — a discount can't exceed what it discounts.
 */
export function redeemableMinor(params) {
    const { config, balanceMinor, orderValueMinor } = params;
    if (balanceMinor < config.minRedeemMinor)
        return 0;
    if (orderValueMinor <= 0)
        return 0;
    const cap = roundMinor((orderValueMinor * config.redeemCapBps) / 10_000);
    return Math.max(0, Math.min(balanceMinor, cap, orderValueMinor));
}
/** Spendable balance right now: unexpired lot remainders. */
export function balanceOfLots(lots, now) {
    return lots.reduce((sum, l) => (l.expiresAt > now ? sum + Math.max(0, l.remainingMinor) : sum), 0);
}
/**
 * Spends `amountMinor` across unexpired lots, oldest-expiry first. Returns how
 * much came from each lot and any shortfall (asked for more than was available).
 * Pure: it computes the plan; the caller writes the lot decrements and ledger.
 */
export function consumeLots(lots, amountMinor, now) {
    const consumed = [];
    let need = Math.max(0, amountMinor);
    const live = lots
        .filter((l) => l.expiresAt > now && l.remainingMinor > 0)
        .slice()
        .sort((a, b) => a.expiresAt - b.expiresAt);
    for (const lot of live) {
        if (need <= 0)
            break;
        const take = Math.min(need, lot.remainingMinor);
        if (take > 0) {
            consumed.push({ id: lot.id, amountMinor: take });
            need -= take;
        }
    }
    return { consumed, shortfallMinor: need };
}
/** Lots that have timed out and still hold value — the daily expiry job's input. */
export function expiredLots(lots, now) {
    return lots.filter((l) => l.expiresAt <= now && l.remainingMinor > 0);
}
/* ------------------------------------------------------------------ *
 * Refund reversal
 * ------------------------------------------------------------------ */
/**
 * When part of an order is refunded, the loyalty side unwinds proportionally:
 * claw back the credits it earned, and give back the credits it spent.
 *
 * `refundFraction` is refundedAmount / paidTotal, clamped to 0..1. The caller
 * caps the clawback at the balance actually available (credits already spent
 * can't be un-granted below zero) and records the shortfall.
 */
export function refundReversal(params) {
    const f = Math.max(0, Math.min(1, params.refundFraction));
    return {
        clawbackMinor: roundMinor((params.earnedMinor ?? 0) * f),
        recreditMinor: roundMinor((params.redeemMinor ?? 0) * f),
    };
}
//# sourceMappingURL=loyalty.js.map